import {
  Box,
  Checkbox,
  IconButton,
  Stack,
  SvgIcon,
  TableCell,
  tableCellClasses,
  TableRow,
  Typography,
} from '@mui/material';
import DotsVerticalIcon from '@untitled-ui/icons-react/build/esm/DotsVertical';
import Download01 from '@untitled-ui/icons-react/build/esm/Download01';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useParams } from 'react-router';
import { usePopover } from 'src/hooks/use-popover';
import type { Item } from 'src/types/file-manager';
import { bytesToSize } from 'src/utils/bytes-to-size';
import { UserRecords } from '../team/team-table';
import { ItemIcon } from './item-icon';
import { ItemMenu } from './item-menu';
import SharedStatus from './shared-status';
import { onDownloadClick } from './utils/getDownloadLink';

interface ItemListRowProps {
  item: Item;
  onDelete?: (itemId: string) => void;
  onFavorite?: (itemId: string, value: boolean) => void;
  onOpen?: (item: Item) => void;
  onShare?: (item: Item) => void;
  onItemChecked?: (item: Item) => void;
  users: UserRecords;
}

export const ItemListRow: FC<ItemListRowProps> = (props) => {
  const { item, onDelete, onFavorite, onOpen, users, onShare, onItemChecked } = props;
  const popover = usePopover<HTMLButtonElement>();

  const handleDelete = useCallback((): void => {
    popover.handleClose();
    onDelete?.(item.id);
  }, [item, popover, onDelete]);

  const { teamId } = useParams();

  // let size = bytesToSize(item.size);

  // if (item.type === 'folder') {
  //   size += `• ${item.itemsCount} items`;
  // }
  const size = item.size && bytesToSize(item.size);
  const createdAt = item.created && format(new Date(item.created), 'MMM dd, yyyy');
  const createdBy = item.createdBy && users[item.createdBy];
  // const showShared = !item.isPublic && (item.shared || []).length > 0;

  return (
    <>
      <TableRow
        key={item.id}
        sx={{
          backgroundColor: 'transparent',
          borderRadius: 1.5,
          boxShadow: 0,
          transition: (theme) =>
            theme.transitions.create(['background-color', 'box-shadow'], {
              easing: theme.transitions.easing.easeInOut,
              duration: 200,
            }),
          '&:hover': {
            backgroundColor: 'background.paper',
            boxShadow: 16,
          },
          [`& .${tableCellClasses.root}`]: {
            borderBottomWidth: 1,
            borderBottomColor: 'divider',
            borderBottomStyle: 'solid',
            borderTopWidth: 1,
            borderTopColor: 'divider',
            borderTopStyle: 'solid',
            '&:first-of-type': {
              borderTopLeftRadius: (theme) => theme.shape.borderRadius * 1.5,
              borderBottomLeftRadius: (theme) => theme.shape.borderRadius * 1.5,
              borderLeftWidth: 1,
              borderLeftColor: 'divider',
              borderLeftStyle: 'solid',
            },
            '&:last-of-type': {
              borderTopRightRadius: (theme) => theme.shape.borderRadius * 1.5,
              borderBottomRightRadius: (theme) => theme.shape.borderRadius * 1.5,
              borderRightWidth: 1,
              borderRightColor: 'divider',
              borderRightStyle: 'solid',
            },
          },
        }}
      >
        <TableCell>
          <Checkbox
            onClick={(e) => {
              onItemChecked?.(item);
            }}
          ></Checkbox>
        </TableCell>
        <TableCell>
          <Stack alignItems="center" direction="row" spacing={2}>
            <Box onClick={() => onOpen?.(item)} sx={{ cursor: 'pointer' }}>
              <ItemIcon type={item.__typename} extension={item.name.split('.').pop()} />
            </Box>
            <div>
              <Typography noWrap onClick={() => onOpen?.(item)} sx={{ cursor: 'pointer' }} variant="subtitle2">
                {item.name}
              </Typography>
              {size && (
                <Typography color="text.secondary" noWrap variant="body2">
                  {size}
                </Typography>
              )}
            </div>
          </Stack>
        </TableCell>
        <TableCell>
          <Typography noWrap variant="subtitle2">
            Created at
          </Typography>
          <Typography color="text.secondary" noWrap variant="body2">
            {createdAt}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography noWrap variant="subtitle2">
            Created by
          </Typography>
          {createdBy && (
            <Typography color="text.secondary" noWrap variant="body2">
              {createdBy.account?.displayName}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex' }}>
            {item.__typename === 'File' && (
              <IconButton onClick={() => onDownloadClick(item, teamId)}>
                <Download01></Download01>
              </IconButton>
            )}
            {/* {item.isPublic && (
              <Tooltip title="Public">
                <Avatar
                  sx={{
                    height: 32,
                    width: 32
                  }}
                >
                  <SvgIcon fontSize="small">
                    <Globe01Icon />
                  </SvgIcon>
                </Avatar>
              </Tooltip>
            )}
            {showShared && (
              <AvatarGroup max={3}>
                {item.shared?.map((person) => (
                  <Avatar
                    key={person.name}
                    src={person.avatar}
                    sx={{
                      height: 32,
                      width: 32
                    }}
                  />
                ))}
              </AvatarGroup>
            )} */}
          </Box>
        </TableCell>
        <TableCell>
          <SharedStatus item={item} onShare={onShare} users={users}></SharedStatus>
        </TableCell>
        {/* <TableCell align="right">
          <IconButton onClick={() => onFavorite?.(item.id, !item.isFavorite)}>
            <SvgIcon
              fontSize="small"
              sx={{ color: item.isFavorite ? 'warning.main' : 'action.active' }}
            >
              <Star01Icon />
            </SvgIcon>
          </IconButton>
        </TableCell> */}

        <TableCell align="right">
          <IconButton onClick={popover.handleOpen} ref={popover.anchorRef}>
            <SvgIcon fontSize="small">
              <DotsVerticalIcon />
            </SvgIcon>
          </IconButton>
        </TableCell>
      </TableRow>
      <ItemMenu
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        onDelete={handleDelete}
        open={popover.open}
      />
    </>
  );
};

ItemListRow.propTypes = {
  // @ts-ignore
  item: PropTypes.object.isRequired,
  onDelete: PropTypes.func,
  onFavorite: PropTypes.func,
  onOpen: PropTypes.func,
};
